import React, { useCallback, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { FormattedMessage } from "react-intl";

import Label from "../Label";
import { FormElement } from "../shared-style";
import Validation from "../Validation";

import messages from "./messages";
import { StyledInput, CardOptions, CardOption, SelectedValue, Restart, Unknown, CreateNewOption } from "./styles";
import { IOption, IProps } from "./interfaces";

const InputSelectCard = ({
    label,
    value,
    disabled,
    name,
    lightWithValue,
    placeholder,
    required,
    options,
    createNew,
    validation,
    simulation,
    unknown,
    simulationInput,
    onChange,
    save,
}: IProps) => {
    const [textValue, setTextValue] = useState("");
    const [isFlipped, setIsFlipped] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const mouseDown = useRef(false);
    useEffect(() => {
        if (simulationInput) {
            setTextValue(simulationInput)
        }
    }, []);

    const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
        let { top } = wrapperRef!.current!.getBoundingClientRect();
        let { innerHeight } = window;
        let flipped = false;
        if (top + 21 > innerHeight / 2 && !simulation) {
            flipped = true;
        }

        setTextValue(e.currentTarget.value)
        setIsFlipped(flipped);
    };
    const select = (id: string) => {
        if (simulation) return;
        onChange(id);
        if (save) save();
        setTextValue("")
    };
    const reset = () => {
        onChange("");
        if (save) save();
        setTimeout(() => {
            inputRef!.current!.focus();
        });
    };
    const blur = () => {
        if (!mouseDown.current && !simulation) {
            setTextValue("");
        }
    };

    const handleMouseUp = useCallback(() => {
        window.removeEventListener("mouseup", handleMouseUp);
        mouseDown.current = false;
    }, [mouseDown]);

    const handleMouseDown = () => {
        mouseDown.current = true;
        window.addEventListener("mouseup", handleMouseUp);
    };

    const filteredOptions: IOption[] = options.filter((option: IOption) => {
        return option.label.toLowerCase().indexOf(textValue.toLowerCase()) !== -1;
    });
    if (filteredOptions.length > 10) {
        filteredOptions.length = 10;
    }

    return (
        <FormElement ref={wrapperRef} style={{ marginBottom: simulation ? 270 : "" }}>
            <Label required={required} disabled={disabled}>
                {label}
                {!disabled && value && (
                    <Restart onClick={reset}>
                        <FormattedMessage {...messages.chooseAnOther} />
                    </Restart>
                )}
                {!value && !disabled && unknown && (
                    <Unknown className="link" onClick={() => select("unknown")}>
                        <FormattedMessage {...messages.unknownComposer} />
                    </Unknown>
                )}
            </Label>
            {validation && <Validation validation={validation} />}
            {!value && (
                <StyledInput
                    className={cn(
                        lightWithValue && value !== "" && "ElementLight",
                        validation && "ValidationState"
                    )}
                    type={"text"}
                    value={textValue}
                    onChange={handleChange}
                    ref={inputRef}
                    disabled={disabled}
                    name={name}
                    onBlur={blur}
                    placeholder={placeholder || ""}
                />
            )}
            {value && options[options.findIndex((option: IOption) => option.id === value)] && options.length > 0 && (
                <SelectedValue>{options[options.findIndex((option: IOption) => option.id === value)].card}</SelectedValue>
            )}
            {value && value === "unknown" && unknown && <FormattedMessage {...messages.unknownComposer} />}
            {textValue.length > 0 && (
                <CardOptions className={isFlipped ? "Flipped" : ""}>
                    {createNew && filteredOptions.length === 0 && (
                        <CreateNewOption
                            onClick={() => {
                                setTextValue("");
                                createNew.onClick();
                            }}
                            onMouseDown={handleMouseDown}
                        >
                            {createNew.label}
                        </CreateNewOption>
                    )}
                    {filteredOptions.length > 0 &&
                        filteredOptions.map(option => (
                            <CardOption
                                key={option.id}
                                onClick={() => select(option.id)}
                                onMouseDown={handleMouseDown}
                            >
                                {option.card}
                            </CardOption>
                        ))}
                </CardOptions>
            )}
        </FormElement>
    );
}

export default InputSelectCard;
